/**
 * WorkerAttendanceApprovals — in-portal attendance approval inbox for a
 * worker who is a team lead / manager (owner 2026-06-26: 考勤审批要在
 * worker portal 内闭环, 不外跳 admin).
 *
 * Reuses the SAME backend endpoint the admin shell + mobile use:
 *   permission holder  GET /v1/attendance/approvals · PATCH /v1/attendance/approvals/{id}
 *   routed reviewer    GET /v1/me/attendance-approvals · POST .../{id}/approve|reject
 *
 * TWO SURFACES, ONE PAGE (2026-09-07). The org-wide queue is RBAC-gated on `attendance.approvals.read`,
 * which the `worker` role must not hold — it returns every punch in the tenant with coordinates. But
 * requests are ROUTED by org chart to the requester's manager, so a team lead on that role was the named
 * approver of requests this page refused. The routed endpoints are authorised per request instead, the
 * same way PureOA's /me/approvals is. Chosen once, below, so list and decision cannot diverge.
 * RBAC-gated server-side (attendance.approvals.read/.manage) — the nav entry
 * renders when useRoleProfile().attendanceReviewer is true (2026-09-07; was canReview), and the endpoint
 * 403s for a plain worker as a backstop.
 *
 * Mobile twin: svdy-mobile/app/attendance-approvals.tsx (same endpoint + the
 * 6 worker-submittable types). Update in lockstep.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert, Avatar, Box, Button, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Paper, Snackbar, Stack, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { ApiError, attendanceApi, meApi } from '../../api';
import { useRoleProfile } from '../../hooks/useRoleProfile';
import type { Approval } from '../../api/attendance';
import WorkerListToolbar from '../../components/worker/WorkerListToolbar';
import { activeLocale } from '../../utils/datetime';
import { attendanceDetailLine } from '../../utils/attendanceRequestDisplay';
import { translateApiError } from '../../i18n/translateApiError';
import { seedStatusFromUrl } from '../../utils/seedStatusFromUrl';

const STATUS_COLOR: Record<string, 'warning' | 'success' | 'error' | 'default'> = {
  pending: 'warning', approved: 'success', rejected: 'error',
};

function initials(name?: string | null): string {
  if (!name) return '•';
  const p = name.trim().split(/\s+/);
  return ((p[0]?.[0] ?? '') + (p.length > 1 ? p[p.length - 1][0] : '')).toUpperCase() || '•';
}

function detailLine(a: Approval, t: (k: string, o?: Record<string, unknown>) => string): string {
  return attendanceDetailLine((a as { data?: Record<string, unknown> }).data, t, undefined, { includeTime: true });
}

function fmtDate(iso?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '—' : d.toLocaleDateString(activeLocale());
}

const WorkerAttendanceApprovals: React.FC<{ embedded?: boolean }> = ({ embedded = false }) => {
  const { t } = useTranslation();
  const [rows, setRows] = useState<Approval[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
    /**
   * The status filter can be SEEDED from `?status=`, because a click on a COUNT must land on the list
   * that count describes. The Overview's pending card links here with `?status=pending`; the sidebar
   * entry links without it and keeps this page's own default.
   *
   * Seed only — the toolbar owns the value afterwards. Syncing both ways would make the URL and the
   * control two sources of truth for one piece of state (svdy-rules §10.5b), and the `?compose=1`
   * precedent in this repo consumes-and-clears for the same reason.
   */
  const [tab, setTab] = useState<'pending' | 'reviewed'>(() => {
    const seeded = seedStatusFromUrl<'pending' | 'reviewed'>('pending');
    return seeded;
  });
  // Owner 2026-09-04: attendance 没有 search，然后 process 中有 search，不一致. Search belongs to
  // any list with a free-text column, decided by the LIST rather than by which module wrote it —
  // the same rule that fixed the status vocabulary. Here the text is the worker's NAME, which is
  // the first thing a reviewer with a queue looks for.
  const [query, setQuery] = useState('');
  const [toast, setToast] = useState<string | null>(null);

  // Decision dialog
  const [selected, setSelected] = useState<Approval | null>(null);
  const [decision, setDecision] = useState<'approved' | 'rejected'>('approved');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  /** WHICH SURFACE, decided once. A permission holder keeps the wider org-wide queue; everyone else
   *  works the queue routed to them. One place, so the list and the decision cannot diverge. */
  const { role } = useRoleProfile();
  const routed = !role?.canReview;
  const fetchRows = useCallback(async (status: 'pending' | 'reviewed') => {
    setLoading(true);
    setError(null);
    try {
      // 'reviewed' = approved + rejected; the endpoint filters one status, so
      // fetch both and merge for the reviewed tab.
      if (status === 'pending') {
        setRows(routed
          ? await meApi.attendanceApprovals.list('pending')
          : await attendanceApi.approvalsApi.list({ status: 'pending' }));
      } else {
        const [ap, rj] = await Promise.all([
          routed ? meApi.attendanceApprovals.list('approved')
                 : attendanceApi.approvalsApi.list({ status: 'approved' }),
          routed ? meApi.attendanceApprovals.list('rejected')
                 : attendanceApi.approvalsApi.list({ status: 'rejected' }),
        ]);
        setRows([...ap, ...rj]);
      }
    } catch (e) {
      setError(e instanceof ApiError ? translateApiError(e, t) : t('worker:attendanceApprovals.loadFailed'));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [t, routed]);

  useEffect(() => { fetchRows(tab); }, [tab, fetchRows]);

  const openDecision = (a: Approval, d: 'approved' | 'rejected') => {
    setSelected(a); setDecision(d); setNote(''); setSubmitError(null);
  };

  const submit = async () => {
    if (!selected) return;
    if (decision === 'rejected' && !note.trim()) {
      setSubmitError(t('worker:attendanceApprovals.reasonRequired'));
      return;
    }
    setSaving(true); setSubmitError(null);
    try {
      const id = (selected as { id: string }).id;
      if (routed) {
        const trimmed = note.trim() || undefined;
        await (decision === 'approved'
          ? meApi.attendanceApprovals.approve(id, trimmed)
          : meApi.attendanceApprovals.reject(id, trimmed));
      } else {
        await attendanceApi.approvalsApi.update(id, {
          status: decision, note: note.trim() || undefined,
        } as never);
      }
      setToast(t(decision === 'approved' ? 'worker:attendanceApprovals.approved' : 'worker:attendanceApprovals.rejected'));
      setSelected(null);
      await fetchRows(tab);
    } catch (e) {
      setSubmitError(e instanceof ApiError ? translateApiError(e, t) : t('common:feedback.actionFailed'));
    } finally {
      setSaving(false);
    }
  };

  const typeLabel = (ty?: string) => t(`worker:attendanceRequests.types.${ty}`, { defaultValue: ty ?? '—' });
  const visibleRows = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const a = r as { worker?: { name?: string; department?: string }; type?: string; data?: unknown };
      const haystack = [
        a.worker?.name, a.worker?.department, a.type,
        // The localized type label too, so searching 请假 finds a `leave` row rather than only
        // the English enum a worker never sees.
        a.type ? t(`worker:attendanceRequests.types.${a.type}`, { defaultValue: '' }) : '',
      ].filter(Boolean).join(' ').toLowerCase();
      return haystack.includes(q);
    });
  }, [rows, query, t]);

  const statusLabel = (s?: string) => t(`worker:attendanceRequests.status.${s}`, { defaultValue: s ?? '—' });

  const pendingCount = useMemo(
    () => rows.filter((r) => (r as { status?: string }).status === 'pending').length,
    [rows],
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', py: embedded ? 0 : '12px', width: '100%' }}>
      {!embedded && (
        <>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.125rem' }}>
            {t('worker:attendanceApprovals.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {t('worker:attendanceApprovals.subtitle')}
          </Typography>
        </>
      )}
      {/* The pending COUNT used to hang off the Pending toggle's own label. It moves to the
          stats slot rather than being dropped: it is the one number a reviewer opens this page
          for, and a label that changes width when you select it is also why the two inboxes
          never looked alike. */}
      <WorkerListToolbar
        set="decided"
        value={tab}
        onChange={(v) => setTab(v as 'pending' | 'reviewed')}
        search={query}
        onSearchChange={setQuery}
        stats={tab === 'pending' && pendingCount > 0 ? (
          <Typography variant="body2" color="text.secondary">
            <strong>{pendingCount}</strong> {t('worker:listFilter.pending').toLowerCase()}
          </Typography>
        ) : undefined}
      />

      {loading ? (
        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress size={28} /></Box>
      ) : error ? (
        <Paper elevation={0} sx={{ width: '100%', p: 4, textAlign: 'center', borderRadius: 2, border: '1px solid', borderColor: 'error.main' }}>
          <Typography variant="body2" color="error">{error}</Typography>
        </Paper>
      ) : visibleRows.length === 0 ? (
        <Paper elevation={0} sx={{ width: '100%', p: 5, textAlign: 'center', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
          <Typography variant="body2" color="text.secondary">
            {rows.length === 0
              ? t('worker:attendanceApprovals.empty')
              : t('worker:attendanceApprovals.noMatch')}
          </Typography>
          <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>
            {rows.length === 0
              ? t('worker:attendanceApprovals.emptyHint')
              : t('worker:attendanceApprovals.noMatchHint')}
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} sx={{ width: '100%', borderRadius: 2, border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
          <Table size="small">
            <TableHead sx={{ bgcolor: 'grey.100', '& .MuiTableCell-root': { fontWeight: 600, fontSize: '0.75rem' } }}>
              <TableRow>
                <TableCell>{t('worker:attendanceApprovals.colWorker')}</TableCell>
                <TableCell>{t('worker:attendanceApprovals.colType')}</TableCell>
                <TableCell>{t('worker:attendanceApprovals.colDetail')}</TableCell>
                <TableCell>{t('worker:attendanceApprovals.colSubmitted')}</TableCell>
                <TableCell align="center">{t('worker:attendanceApprovals.colStatus')}</TableCell>
                <TableCell align="right">{t('worker:attendanceApprovals.colAction')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleRows.map((r) => {
                const a = r as unknown as { id: string; type?: string; status?: string; worker?: { name?: string; department?: string } | null; submitted_at?: string; approval_chain?: { level: string; status: string }[] | null; current_step?: number };
                const wname = a.worker?.name ?? '—';
                const wdept = a.worker?.department ?? '';
                const chain = a.approval_chain;
                const step = a.current_step ?? 0;
                const progress = chain && chain.length > 1
                  ? t('worker:attendanceApprovals.stepProgress', {
                      n: step + 1, total: chain.length,
                      level: t(`worker:attendanceApprovals.level.${chain[step]?.level}`, { defaultValue: chain[step]?.level }),
                    })
                  : null;
                return (
                  <TableRow key={a.id} hover>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Avatar sx={{ width: 24, height: 24, fontSize: '0.65rem', bgcolor: 'primary.main' }}>{initials(wname)}</Avatar>
                        <Box>
                          <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>{wname}</Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>{wdept}</Typography>
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">{typeLabel(a.type)}</Typography>
                      {progress && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.62rem' }}>
                          {progress}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell><Typography variant="caption">{detailLine(r, t) || '—'}</Typography></TableCell>
                    <TableCell><Typography variant="caption">{fmtDate(a.submitted_at)}</Typography></TableCell>
                    <TableCell align="center">
                      <Chip size="small" variant="outlined" color={STATUS_COLOR[a.status ?? ''] ?? 'default'} label={statusLabel(a.status)} sx={{ borderRadius: 1, height: 20, fontSize: '0.65rem' }} />
                    </TableCell>
                    <TableCell align="right">
                      {a.status === 'pending' ? (
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                          <Button size="small" variant="contained" color="success" disableElevation onClick={() => openDecision(r, 'approved')}
                            sx={{ textTransform: 'none', fontSize: '0.7rem', borderRadius: 1, py: 0 }}>
                            {t('worker:attendanceApprovals.approve')}
                          </Button>
                          <Button size="small" variant="outlined" color="warning" onClick={() => openDecision(r, 'rejected')}
                            sx={{ textTransform: 'none', fontSize: '0.7rem', borderRadius: 1, py: 0 }}>
                            {t('worker:attendanceApprovals.reject')}
                          </Button>
                        </Stack>
                      ) : (
                        <Typography variant="caption" color="text.disabled">—</Typography>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Decision dialog (svdy-ui §5 — mutations in a centered Dialog). */}
      <Dialog open={!!selected} onClose={() => !saving && setSelected(null)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
        {selected && (
          <>
            <DialogTitle sx={{ py: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: '1rem' }}>
                {decision === 'approved' ? t('worker:attendanceApprovals.approveTitle') : t('worker:attendanceApprovals.rejectTitle')}
              </Typography>
            </DialogTitle>
            <DialogContent dividers sx={{ py: 3 }}>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="caption" color="text.secondary">{typeLabel((selected as { type?: string }).type)}</Typography>
                  <Typography variant="body2">{detailLine(selected, t) || '—'}</Typography>
                </Box>
                {submitError && <Alert severity="error" variant="outlined" sx={{ fontSize: '0.8rem' }} onClose={() => setSubmitError(null)}>{submitError}</Alert>}
                <TextField
                  size="small" fullWidth multiline rows={2}
                  required={decision === 'rejected'}
                  label={decision === 'rejected' ? t('worker:attendanceApprovals.reasonLabel') : t('worker:attendanceApprovals.noteLabel')}
                  value={note} onChange={(e) => setNote(e.target.value)}
                />
              </Stack>
            </DialogContent>
            <DialogActions sx={{ py: 2 }}>
              <Button onClick={() => setSelected(null)} disabled={saving} sx={{ borderRadius: 1, textTransform: 'none' }}>
                {t('common:actions.cancel')}
              </Button>
              <Button variant="contained" color={decision === 'approved' ? 'success' : 'warning'} disableElevation
                onClick={submit} disabled={saving || (decision === 'rejected' && !note.trim())}
                sx={{ borderRadius: 1, textTransform: 'none' }}>
                {saving ? <CircularProgress size={18} /> : (decision === 'approved' ? t('worker:attendanceApprovals.approve') : t('worker:attendanceApprovals.reject'))}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={4000} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="success" onClose={() => setToast(null)} sx={{ width: '100%' }}>{toast}</Alert>
      </Snackbar>
    </Box>
  );
};

export default WorkerAttendanceApprovals;
